import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient } from '../api';
import { IconOk, IconWarnung } from '../Icons';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ConfirmDialog, ContentSection, EmptyState, ErrorState, Feld, KpiCard,
  KpiReihe, LoadingState,
} from '../muster';

interface Rahmen { api: ApiClient }

type Bereich = 'belastbarkeit' | 'faktoren' | 'grenzwerte'
              | 'anschluss';

const BEREICH_NAME: Record<Bereich, string> = {
  belastbarkeit: 'Strombelastbarkeit',
  faktoren: 'Korrekturfaktoren',
  grenzwerte: 'Grenzwerte',
  anschluss: 'Anschlusswerte',
};

/**
 * Die Normwerte prüfen und freigeben.
 *
 * --- Warum diese Seite existiert -------------------------------------------------
 *
 * Die Werte stammen aus einer Nachbildung, nicht aus einer Normausgabe. Solange sie
 * ungeprüft sind, meldet das Qualitätsgate jede Verwendung als BLOCKER, und jede
 * Planseite trägt „VORABZUG — NICHT ZUR AUSFÜHRUNG".
 *
 * Hier wird das aufgehoben — Zeile für Zeile, gegen die Normausgabe gehalten.
 *
 * --- Was eine Freigabe bedeutet --------------------------------------------------
 *
 * Wer eine Zeile freigibt, übernimmt die Verantwortung dafür, dass der Wert der
 * genannten Ausgabe entspricht. Deshalb verlangt die Schnittstelle einen Normbezug
 * mit Jahresangabe: „DIN VDE 0298-4" allein sagt nicht, welche Fassung gemeint war,
 * und die Werte haben sich zwischen den Ausgaben geändert.
 *
 * --- Was zuerst zu prüfen ist ----------------------------------------------------
 *
 * Nicht alles. Es genügen die Zeilen, die das Projekt tatsächlich braucht — bei
 * einem Bürogebäude sind das die Querschnitte 1,5 bis 16 mm² in einer oder zwei
 * Verlegearten, die Temperaturfaktoren um 30 °C und die Grenzwerte.
 *
 * Die als UNSICHER gekennzeichneten zuerst: Dort war ich mir der Ziffer selbst
 * nicht sicher.
 */
export function Normwerte() {
  const { api } = useOutletContext<Rahmen>();

  const [daten, setDaten] = useState<Awaited<
    ReturnType<ApiClient['normwerte']>> | null>(null);

  const [bereich, setBereich] = useState<Bereich>('belastbarkeit');
  const [nurOffen, setNurOffen] = useState(true);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [ruecknahmeFrage, setRuecknahmeFrage] = useState(false);
  const [ruecknahmeLaeuft, setRuecknahmeLaeuft] = useState(false);

  const laden = async () => {
    try {
      setDaten(await api.normwerte());
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => { laden(); }, []);

  if (fehler) {
    return (
      <>
        <PageHeader titel="Normwerte"
          beschreibung="Nachgebildete Normwerte gegen die Normausgabe halten und freigeben." />
        <ErrorState
          meldung="Die Normwerte konnten nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={laden}
        />
      </>
    );
  }

  if (!daten) {
    return (
      <>
        <PageHeader titel="Normwerte"
          beschreibung="Nachgebildete Normwerte gegen die Normausgabe halten und freigeben." />
        <LoadingState text="Normwerte werden geladen …" zeilen={10} spalten={4} />
      </>
    );
  }

  const { stand } = daten;
  const offen = stand.gesamt - stand.geprueft;

  const sichtbareZeilen = (bereich === 'belastbarkeit' ? daten.belastbarkeit
    : bereich === 'faktoren' ? daten.faktoren
      : bereich === 'grenzwerte' ? daten.grenzwerte : [])
    .filter((x) => !nurOffen || !x.geprueft).length;

  return (
    <>
      <PageHeader
        titel="Normwerte"
        beschreibung={'Die Werte stammen aus einer Nachbildung, nicht aus einer '
          + 'Normausgabe. Wer eine Zeile freigibt, übernimmt die Verantwortung dafür, '
          + 'dass der Wert der genannten Ausgabe entspricht.'}
        meta={`${stand.geprueft} von ${stand.gesamt} geprüft`}
        aktionen={stand.geprueft > 0 ? (
          <button type="button" className="m-knopf"
            onClick={() => setRuecknahmeFrage(true)}>
            alle {stand.geprueft} Freigaben zurücknehmen
          </button>
        ) : undefined}
      />

      <KpiReihe>
        <KpiCard
          titel="Geprüft und freigegeben" wert={stand.geprueft}
          vergleich={`von ${stand.gesamt} Werten`}
          zustand={offen > 0 ? 'warnung' : 'gut'}
          zustandText={offen > 0 ? `${offen} offen` : 'vollständig'}
        />
        <KpiCard
          titel="Nicht geprüft" wert={offen}
          fussnote="Jede Verwendung meldet das Qualitätsgate als BLOCKER."
          zustand={offen > 0 ? 'warnung' : 'gut'}
          zustandText={offen > 0 ? 'Vorabzugsvermerk aktiv' : 'kein Vorabzugsvermerk'}
        />
        <KpiCard
          titel="Als UNSICHER gekennzeichnet" wert={stand.alsUnsicher}
          fussnote="Dort war die Ziffer selbst unsicher — der niedrigere Wert wurde gewählt."
          zustand={stand.alsUnsicher > 0 ? 'warnung' : undefined}
          zustandText={stand.alsUnsicher > 0 ? 'zuerst prüfen' : undefined}
        />
      </KpiReihe>

      {/* --- Der Stand, und was er bedeutet -------------------------------- */}
      <ContentSection titel="Was der Stand bedeutet">
        {offen > 0 ? (
          <>
            <h3 style={{ marginTop: 0, fontSize: 14, fontWeight: 500 }}>
              <IconWarnung size={14} /> {offen} Werte sind nicht geprüft
            </h3>

            <p className="schwach" style={{ fontSize: 12, maxWidth: 760 }}>
              Sie stammen aus einer Nachbildung, nicht aus einer Normausgabe.
              Solange ein <em>verwendeter</em> Wert ungeprüft ist, meldet das
              Qualitätsgate einen BLOCKER, und jede Planseite trägt
              {' '}<strong>„VORABZUG — NICHT ZUR AUSFÜHRUNG"</strong>.
            </p>

            <p className="schwach" style={{ fontSize: 12, maxWidth: 760 }}>
              Es müssen nicht alle geprüft werden — nur die, die das Projekt
              braucht. Bei einem Bürogebäude sind das die Querschnitte 1,5 bis
              16 mm² in einer oder zwei Verlegearten, die Temperaturfaktoren um
              30 °C und die Grenzwerte.
            </p>

            {stand.alsUnsicher > 0 && (
              <p style={{ fontSize: 12, maxWidth: 760 }}>
                <strong>{stand.alsUnsicher} Werte sind als UNSICHER
                gekennzeichnet</strong> — dort war ich mir der Ziffer selbst nicht
                sicher und habe den niedrigeren, also sicheren Wert gewählt. Diese
                zuerst.
              </p>
            )}
          </>
        ) : (
          <h3 style={{ marginTop: 0, fontSize: 14, fontWeight: 500 }}>
            <IconOk size={14} /> Alle {stand.gesamt} Werte sind geprüft und
            freigegeben.
          </h3>
        )}
      </ContentSection>

      {meldung && (
        <div className="hinweis" style={{ marginBottom: 12 }}>
          {meldung}
          <button type="button" className="m-knopf" onClick={() => setMeldung(null)}
                  style={{ marginLeft: 8 }}>
            schließen
          </button>
        </div>
      )}

      {/* --- Die Auswahl -------------------------------------------------- */}
      <ContentSection titel="Bereich" nackt>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap',
                      alignItems: 'center' }}>
          {([
            ['belastbarkeit', `Strombelastbarkeit (${daten.belastbarkeit.length})`],
            ['faktoren', `Korrekturfaktoren (${daten.faktoren.length})`],
            ['grenzwerte', `Grenzwerte (${daten.grenzwerte.length})`],
            ['anschluss', `Anschlusswerte (${daten.anschlusswerte.length})`],
          ] as [Bereich, string][]).map(([schluessel, name]) => (
            <button
              key={schluessel}
              type="button"
              onClick={() => setBereich(schluessel)}
              className={bereich === schluessel ? 'm-knopf m-knopf-haupt' : 'm-knopf'}
            >
              {name}
            </button>
          ))}

          <span style={{ flex: 1 }} />

          <Feld art="haken" beschriftung="nur ungeprüfte" wert={nurOffen}
            beiAenderung={(w) => setNurOffen(w as boolean)} />
        </div>
      </ContentSection>

      {/* --- Die Anschlusswerte, eigene Tabelle ---------------------------- */}
      {/*
        Nicht in der gemeinsamen Tabelle: Ein Anschlusswert hat ZWEI Zahlen mit
        verschiedener Herkunft, und die gemeinsame Zeile zeigt eine.

          Anschlussleistung   gerechnet aus dem Nennstrom — für Leitung und
                              Spannungsfall
          Bedarfsansatz       Erfahrungswert — für die Bilanz an NSHV und Trafo

        Sie in einer Spalte zu zeigen hieße, die Verwechslung einzubauen, vor der
        die Bemerkung warnt.
      */}
      {bereich === 'anschluss' && (
        <ContentSection titel="Anschlusswerte"
          meta={`${daten.anschlusswerte.filter((x) => !nurOffen || !x.geprueft).length} Zeilen`}>
          <div className="sehr-schwach" style={{ fontSize: 11, marginBottom: 8 }}>
            <strong>Zwei Zahlen, zwei Ebenen.</strong> Die Anschlussleistung ist aus
            dem Nennstrom <strong>gerechnet</strong> — S = U × I bzw. √3 × U × I — und
            gilt für Leitung, Schutzorgan, Spannungsfall und Abschaltbedingung nach
            DIN VDE 0100-430 und -520. Der Bedarfsansatz ist ein{' '}
            <strong>Erfahrungswert</strong> für die Bilanz an NSHV und Trafo:
            P = n × Bedarf × g.
          </div>

          <div className="hinweis" style={{ marginBottom: 8, fontSize: 11 }}>
            <IconWarnung size={13} />
            <div>
              Fünf Steckdosen an einem B16-Kreis sind <strong>nicht</strong> 5 × 3,68
              kVA — der Kreis liefert höchstens 3,68 kVA, das ist sein Schutzorgan.
              Gleichzeitigkeitsfaktoren dürfen erst bei <strong>übergeordneten</strong>
              {' '}Verteilungen verwendet werden. Und es gibt keinen normativ
              festgelegten pauschalen Wattwert je Steckdose.
            </div>
          </div>

          <div className="tabellenrahmen">
            <table className="zuordnung" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Anschluss</th>
                  <th style={{ textAlign: 'right', width: 54 }}>U</th>
                  <th style={{ textAlign: 'right', width: 34 }}>Ph</th>
                  <th style={{ textAlign: 'right', width: 54 }}>In</th>
                  <th style={{ textAlign: 'right', width: 84 }}>Anschluss</th>
                  <th style={{ textAlign: 'right', width: 84 }}>Bedarf</th>
                  <th style={{ textAlign: 'right', width: 54 }}>cos φ</th>
                  <th style={{ textAlign: 'right', width: 54 }}>g</th>
                  <th style={{ width: 40 }}>RCD</th>
                  <th style={{ width: 110 }} />
                </tr>
              </thead>
              <tbody>
                {daten.anschlusswerte
                  .filter((x) => !nurOffen || !x.geprueft)
                  .map((x) => (
                    <Anschlusszeile
                      key={x.id}
                      api={api}
                      wert={x}
                      fertig={(m) => { setMeldung(m); laden(); }}
                    />
                  ))}
              </tbody>
            </table>
          </div>

          {daten.anschlusswerte.filter((x) => !nurOffen || !x.geprueft).length === 0 && (
            <EmptyState
              titel={nurOffen
                ? 'Alle Anschlusswerte sind geprüft.'
                : 'Es ist kein Anschlusswert hinterlegt.'}
              erklaerung={nurOffen
                ? 'Der Haken „nur ungeprüfte" blendet die freigegebenen Zeilen aus.'
                : 'Ohne Anschlusswerte rechnet die Elektroplanung keinen Bedarf.'}
              aktion={nurOffen ? (
                <button type="button" className="m-knopf" onClick={() => setNurOffen(false)}>
                  auch geprüfte zeigen
                </button>
              ) : undefined}
            />
          )}
        </ContentSection>
      )}

      {/* --- Die Zeilen --------------------------------------------------- */}
      {bereich !== 'anschluss' && (
      <ContentSection titel={BEREICH_NAME[bereich]}>
        <div className="tabellenrahmen">
          <table className="zuordnung" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Zeile</th>
                <th style={{ textAlign: 'right', width: 90 }}>Wert</th>
                <th style={{ textAlign: 'left' }}>Normbezug</th>
                <th style={{ width: 120 }} />
              </tr>
            </thead>
            <tbody>
              {bereich === 'belastbarkeit' && daten.belastbarkeit
                .filter((x) => !nurOffen || !x.geprueft)
                .map((x) => (
                  <Zeile
                    key={x.id}
                    api={api}
                    weg={`belastbarkeit/${x.id}`}
                    name={`${x.verlegeart} · ${x.belasteteAdern} Adern · `
                        + `${x.werkstoff}/${x.isolierung} · ${x.querschnitt} mm²`}
                    wert={x.iz}
                    einheit="A"
                    normbezug={x.normbezug}
                    geprueft={x.geprueft}
                    geprueftVon={x.geprueftVon}
                    bemerkung={x.bemerkung}
                    fertig={(m) => { setMeldung(m); laden(); }}
                  />
                ))}

              {bereich === 'faktoren' && daten.faktoren
                .filter((x) => !nurOffen || !x.geprueft)
                .map((x) => (
                  <Zeile
                    key={x.id}
                    api={api}
                    weg={`faktoren/${x.id}`}
                    name={`${x.art} · ${x.kennwert}`
                        + (x.bedingung ? ` · ${x.bedingung}` : '')}
                    wert={x.faktor}
                    einheit=""
                    normbezug={x.normbezug}
                    geprueft={x.geprueft}
                    geprueftVon={x.geprueftVon}
                    bemerkung={null}
                    fertig={(m) => { setMeldung(m); laden(); }}
                  />
                ))}

              {bereich === 'grenzwerte' && daten.grenzwerte
                .filter((x) => !nurOffen || !x.geprueft)
                .map((x) => (
                  <Zeile
                    key={x.id}
                    api={api}
                    weg={`grenzwerte/${x.id}`}
                    name={`${x.schluessel}`
                        + (x.bedingung ? ` · ${x.bedingung}` : '')}
                    hinweis={x.bezeichnung}
                    wert={x.wert}
                    einheit={x.einheit ?? ''}
                    normbezug={x.normbezug}
                    geprueft={x.geprueft}
                    geprueftVon={x.geprueftVon}
                    bemerkung={x.bemerkung}
                    fertig={(m) => { setMeldung(m); laden(); }}
                  />
                ))}
            </tbody>
          </table>
        </div>

        {sichtbareZeilen === 0 && (
          <EmptyState
            titel={nurOffen
              ? `Alle Werte in „${BEREICH_NAME[bereich]}" sind geprüft.`
              : `In „${BEREICH_NAME[bereich]}" ist kein Wert hinterlegt.`}
            erklaerung={nurOffen
              ? 'Der Haken „nur ungeprüfte" blendet die freigegebenen Zeilen aus.'
              : 'Die Normwerte kommen aus der Nachbildung des Katalogs.'}
            aktion={nurOffen ? (
              <button type="button" className="m-knopf" onClick={() => setNurOffen(false)}>
                auch geprüfte zeigen
              </button>
            ) : undefined}
          />
        )}
      </ContentSection>

      )}

      {/* --- Die Rücknahme ------------------------------------------------ */}
      {stand.geprueft > 0 && (
        <ContentSection titel="Freigaben zurücknehmen">
          <p className="schwach" style={{ fontSize: 12, maxWidth: 720 }}>
            Zeigt sich, dass eine Freigabe auf einer falschen Normausgabe beruhte,
            lässt sie sich zurücknehmen. Danach gelten alle Werte wieder als
            nachgebildet, und jeder Plan trägt den Vorabzugsvermerk.
            {' '}<em>Ohne diesen Weg wäre eine Freigabe unumkehrbar — und niemand
            würde sie dann noch aussprechen.</em>
          </p>

          <button type="button" className="m-knopf"
            onClick={() => setRuecknahmeFrage(true)}>
            alle {stand.geprueft} Freigaben zurücknehmen
          </button>
        </ContentSection>
      )}

      <ConfirmDialog
        offen={ruecknahmeFrage}
        art="warnung"
        titel="Alle Freigaben zurücknehmen?"
        text={`${stand.geprueft} Werte gelten danach wieder als nachgebildet, und jeder `
          + 'Plan trägt den Vorabzugsvermerk „VORABZUG — NICHT ZUR AUSFÜHRUNG".'}
        bestaetigenText="Zurücknehmen"
        laeuft={ruecknahmeLaeuft}
        beiBestaetigen={async () => {
          setRuecknahmeLaeuft(true);
          try {
            const a = await api.normwerteFreigabeZuruecknehmen();
            setMeldung(a ? a.message : 'Zurückgenommen.');
            await laden();
          } catch (e) {
            setMeldung(e instanceof Error ? e.message : String(e));
          } finally {
            setRuecknahmeLaeuft(false);
            setRuecknahmeFrage(false);
          }
        }}
        beiAbbrechen={() => setRuecknahmeFrage(false)}
      />
    </>
  );
}

// === Eine Zeile ==============================================================

function Zeile({
  api, weg, name, hinweis, wert, einheit, normbezug, geprueft, geprueftVon,
  bemerkung, fertig,
}: {
  api: ApiClient;
  weg: string;
  name: string;
  hinweis?: string;
  wert: number;
  einheit: string;
  normbezug: string | null;
  geprueft: boolean;
  geprueftVon: string | null;
  bemerkung: string | null;
  fertig: (m: string) => void;
}) {
  const [entwurfWert, setEntwurfWert] = useState(String(wert));
  const [entwurfBezug, setEntwurfBezug] = useState(normbezug ?? '');
  const [laeuft, setLaeuft] = useState(false);

  const unsicher = bemerkung?.startsWith('UNSICHER') ?? false;

  const geaendert = entwurfWert !== String(wert)
                 || entwurfBezug !== (normbezug ?? '');

  // Die Freigabe verlangt eine Jahresangabe im Normbezug. Hier schon prüfen,
  // damit der Knopf nicht in eine Fehlermeldung läuft.
  const bezugTaugt = /\d{4}/.test(entwurfBezug);

  const senden = async (freigeben: boolean) => {
    setLaeuft(true);
    try {
      const zahl = Number(entwurfWert.replace(',', '.'));

      if (!Number.isFinite(zahl)) {
        fertig('Der Wert ist keine Zahl.');
        return;
      }

      const a = await api.normwertSetzen(weg, {
        wert: zahl,
        normbezug: entwurfBezug || null,
        geprueft: freigeben,
        bemerkung: freigeben ? null : bemerkung,
      });

      fertig(a ? a.message : 'Gesetzt.');
    } catch (e) {
      fertig(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <tr style={{ background: unsicher && !geprueft
      ? 'var(--hellgelb, #fffbe6)' : undefined }}>
      <td style={{ fontSize: 12 }}>
        {geprueft && <IconOk size={11} />}
        <span style={{ marginLeft: geprueft ? 5 : 0 }}>{name}</span>

        {hinweis && (
          <div className="sehr-schwach" style={{ fontSize: 10 }}>{hinweis}</div>
        )}

        {unsicher && !geprueft && (
          <div style={{ fontSize: 10, marginTop: 2 }}>{bemerkung}</div>
        )}

        {geprueft && geprueftVon && (
          <div className="sehr-schwach" style={{ fontSize: 10 }}>
            freigegeben von {geprueftVon}
          </div>
        )}
      </td>

      <td style={{ textAlign: 'right' }}>
        <input
          value={entwurfWert}
          onChange={(e) => setEntwurfWert(e.target.value)}
          inputMode="decimal"
          style={{ width: 56, fontSize: 12, textAlign: 'right' }}
        />
        <span className="sehr-schwach" style={{ fontSize: 10, marginLeft: 3 }}>
          {einheit}
        </span>
      </td>

      <td>
        <input
          value={entwurfBezug}
          onChange={(e) => setEntwurfBezug(e.target.value)}
          placeholder="DIN VDE 0298-4:2013-06 Tabelle 11 Spalte 5"
          style={{ width: '100%', fontSize: 11 }}
        />

        {!bezugTaugt && entwurfBezug.length > 0 && (
          <div className="sehr-schwach" style={{ fontSize: 10 }}>
            Die Ausgabe fehlt — bitte das Jahr angeben.
          </div>
        )}
      </td>

      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {geprueft ? (
          <span className="sehr-schwach" style={{ fontSize: 10 }}>
            freigegeben
          </span>
        ) : (
          <>
            <button
              onClick={() => senden(true)}
              disabled={laeuft || !bezugTaugt}
              className="betont"
              style={{ fontSize: 10, padding: '1px 6px' }}
              title={bezugTaugt
                ? 'Wert übernehmen und freigeben'
                : 'Für eine Freigabe ist der Normbezug mit Ausgabe erforderlich'}
            >
              freigeben
            </button>

            {geaendert && (
              <button
                onClick={() => senden(false)}
                disabled={laeuft}
                style={{ fontSize: 10, padding: '1px 6px', marginLeft: 4 }}
                title="Nur den Wert setzen, ohne Freigabe"
              >
                nur setzen
              </button>
            )}
          </>
        )}
      </td>
    </tr>
  );
}

// === Eine Anschlusswertzeile ================================================

/**
 * Ein Anschlusswert mit seinen zwei Zahlen.
 *
 * --- Warum die Anschlussleistung nicht eingebbar ist ------------------------------
 *
 * Sie folgt aus dem Nennstrom: S = U × I bzw. √3 × U × I. Wer sie von Hand ändert,
 * widerspricht der Physik — und der Server rechnet sie beim Speichern ohnehin neu.
 *
 * Deshalb steht sie hier als Text, nicht als Feld. Eingebbar sind Spannung, Phasen
 * und Nennstrom; die Leistung ergibt sich.
 *
 * --- Und der Bedarfsansatz ist eingebbar ----------------------------------------
 *
 * Er ist ein Erfahrungswert, keine Rechnung. Bei 1.670 Steckdosen entscheidet er über
 * die Trafogröße: 2,0 kW bei g = 0,4 ergeben 1.336 kW, 3,0 kW ergeben 2.004 kW.
 */
function Anschlusszeile({
  api, wert, fertig,
}: {
  api: ApiClient;
  wert: {
    id: string; schluessel: string; bezeichnung: string;
    spannung: number; phasen: number; nennstrom: number;
    anschlussleistung: number; bedarfsansatz: number | null;
    leistungsfaktor: number | null;
    gleichzeitigEinzel: number | null; gleichzeitigGruppe: number | null;
    brauchtRcd: boolean;
    normbezug: string | null; herkunft: string; geprueft: boolean;
    geprueftVon: string | null; bemerkung: string | null;
  };
  fertig: (m: string) => void;
}) {
  const [offen, setOffen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);

  const [eingabe, setEingabe] = useState({
    nennstrom: String(wert.nennstrom),
    bedarfsansatz: wert.bedarfsansatz != null ? String(wert.bedarfsansatz) : '',
    leistungsfaktor: wert.leistungsfaktor != null
      ? String(wert.leistungsfaktor) : '',
    gleichzeitigGruppe: wert.gleichzeitigGruppe != null
      ? String(wert.gleichzeitigGruppe) : '',
  });

  const speichern = async () => {
    setLaeuft(true);

    try {
      const a = await api.anschlusswertSetzen(wert.id, {
        nennstrom: Number(eingabe.nennstrom.replace(',', '.')) || null,
        bedarfsansatz: eingabe.bedarfsansatz
          ? Number(eingabe.bedarfsansatz.replace(',', '.')) : null,
        leistungsfaktor: eingabe.leistungsfaktor
          ? Number(eingabe.leistungsfaktor.replace(',', '.')) : null,
        gleichzeitigGruppe: eingabe.gleichzeitigGruppe
          ? Number(eingabe.gleichzeitigGruppe.replace(',', '.')) : null,
      });

      fertig(a ? a.message : 'Gespeichert.');
      setOffen(false);
    } catch (e) {
      fertig(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <>
      <tr>
        <td style={{ fontSize: 11 }}>
          {wert.bezeichnung}

          {/* Die Herkunft je Zeile. 'gemischt' heißt: die eine Zahl gerechnet,
              die andere geschätzt — und das ist bei einem Anschlusswert der
              Normalfall, kein Mangel. */}
          <div className="sehr-schwach" style={{ fontSize: 9 }}>
            {wert.herkunft === 'gemischt'
              ? 'Anschluss gerechnet · Bedarf geschätzt'
              : wert.herkunft}
            {wert.geprueftVon && ` · ${wert.geprueftVon}`}
          </div>
        </td>

        <td style={{ textAlign: 'right', fontSize: 11 }}>{wert.spannung} V</td>
        <td style={{ textAlign: 'right', fontSize: 11 }}>{wert.phasen}</td>
        <td style={{ textAlign: 'right', fontSize: 11 }}>{wert.nennstrom} A</td>

        {/* Gerechnet — deshalb nicht als Feld, sondern als Wert mit Hinweis. */}
        <td style={{ textAlign: 'right', fontSize: 11 }}
            title="Gerechnet aus dem Nennstrom: S = U × I bzw. √3 × U × I. Für Leitung, Schutzorgan, Spannungsfall und Abschaltbedingung.">
          <strong>{(wert.anschlussleistung / 1000).toFixed(2)}</strong>
          <span className="sehr-schwach"> kVA</span>
        </td>

        <td style={{ textAlign: 'right', fontSize: 11 }}
            title="Erfahrungswert für die Bilanz an NSHV und Trafo: P = n × Bedarf × g. KEIN Normwert.">
          {wert.bedarfsansatz != null
            ? <>{(wert.bedarfsansatz / 1000).toFixed(2)}
                <span className="sehr-schwach"> kW</span></>
            : <span className="sehr-schwach">—</span>}
        </td>

        <td style={{ textAlign: 'right', fontSize: 11 }}>
          {wert.leistungsfaktor?.toFixed(2) ?? '—'}
        </td>

        <td style={{ textAlign: 'right', fontSize: 11 }}
            title="Gleichzeitigkeit in der Gruppe — nur auf übergeordneter Verteilungsebene zulässig, nicht am Stromkreis.">
          {wert.gleichzeitigGruppe?.toFixed(2) ?? '—'}
        </td>

        <td style={{ textAlign: 'center', fontSize: 10 }}
            title={wert.brauchtRcd
              ? '30-mA-RCD nach DIN VDE 0100-410 grundsätzlich erforderlich'
              : 'Über 32 A gilt der pauschale Satz nicht mehr — das heißt NICHT, dass kein Zusatzschutz nötig ist'}>
          {wert.brauchtRcd
            ? <span className="marke gut" style={{ fontSize: 9 }}>30 mA</span>
            : <span className="sehr-schwach">—</span>}
        </td>

        <td style={{ textAlign: 'right' }}>
          <button onClick={() => setOffen(!offen)} disabled={laeuft}
                  style={{ fontSize: 10, padding: '1px 6px' }}>
            {offen ? 'schließen' : 'bearbeiten'}
          </button>
        </td>
      </tr>

      {offen && (
        <tr>
          <td colSpan={10} style={{ padding: '6px 12px',
                                    background: 'var(--flaeche, #fafafa)' }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap',
                          alignItems: 'flex-end' }}>
              <label style={{ width: 90 }}>
                <div className="sehr-schwach" style={{ fontSize: 10 }}>
                  Nennstrom A
                </div>
                <input value={eingabe.nennstrom} inputMode="decimal"
                       onChange={(e) => setEingabe({ ...eingabe,
                                                     nennstrom: e.target.value })}
                       style={{ width: '100%', fontSize: 12 }} />
              </label>

              <label style={{ width: 110 }}>
                <div className="sehr-schwach" style={{ fontSize: 10 }}>
                  Bedarfsansatz W
                </div>
                <input value={eingabe.bedarfsansatz} inputMode="decimal"
                       onChange={(e) => setEingabe({ ...eingabe,
                                                     bedarfsansatz: e.target.value })}
                       style={{ width: '100%', fontSize: 12 }} />
              </label>

              <label style={{ width: 80 }}>
                <div className="sehr-schwach" style={{ fontSize: 10 }}>cos φ</div>
                <input value={eingabe.leistungsfaktor} inputMode="decimal"
                       onChange={(e) => setEingabe({ ...eingabe,
                                                     leistungsfaktor: e.target.value })}
                       style={{ width: '100%', fontSize: 12 }} />
              </label>

              <label style={{ width: 100 }}>
                <div className="sehr-schwach" style={{ fontSize: 10 }}>
                  g Gruppe
                </div>
                <input value={eingabe.gleichzeitigGruppe} inputMode="decimal"
                       onChange={(e) => setEingabe({
                         ...eingabe, gleichzeitigGruppe: e.target.value })}
                       style={{ width: '100%', fontSize: 12 }} />
              </label>

              <button onClick={speichern} disabled={laeuft} className="betont"
                      style={{ fontSize: 12 }}>
                speichern
              </button>
            </div>

            <div className="sehr-schwach" style={{ fontSize: 10, marginTop: 6 }}>
              Die <strong>Anschlussleistung wird nachgerechnet</strong> — sie ist kein
              Eingabefeld. Wer den Nennstrom ändert, bekommt die neue Leistung mit.
            </div>

            {wert.bemerkung && (
              <div className="schwach" style={{ fontSize: 10, marginTop: 8,
                                                lineHeight: 1.5, maxWidth: 900 }}>
                {wert.bemerkung}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
